import React from 'react';

interface LoaderOverlayProps {
  isVisible: boolean;
  message?: string;
  backdrop?: 'dark' | 'light' | 'blur';
  variant?: 'spinner' | 'dots' | 'pulse' | 'bars';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'white' | 'gray';
}

const LoaderOverlay: React.FC<LoaderOverlayProps> = ({
  isVisible,
  message,
  backdrop = 'dark',
  variant = 'spinner',
  size = 'lg',
  color = 'primary'
}) => {
  if (!isVisible) return null;

  const backdropClasses = {
    dark: 'bg-black bg-opacity-50',
    light: 'bg-white bg-opacity-70',
    blur: 'bg-black bg-opacity-30 backdrop-blur-sm'
  };

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    primary: 'text-blue-600',
    secondary: 'text-gray-600',
    white: 'text-white',
    gray: 'text-gray-400'
  };

  const renderLoader = () => {
    const baseClasses = `${sizeClasses[size]} ${colorClasses[color]}`;

    switch (variant) {
      case 'spinner':
        return (
          <svg
            className={`animate-spin ${baseClasses}`}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        );

      case 'dots':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-pulse bg-current`}
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        );

      case 'pulse':
        return (
          <div
            className={`${baseClasses} rounded-full bg-current animate-pulse opacity-75`}
          />
        );

      case 'bars':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-1 ${sizeClasses[size]} ${colorClasses[color]} bg-current animate-bounce`}
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${backdropClasses[backdrop]}`}>
      <div className="flex flex-col items-center space-y-4">
        {renderLoader()}
        {message && (
          <p className="text-sm font-medium text-white max-w-xs text-center">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoaderOverlay;
